import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import LineSignShape from "../../../../components/visuals/LineSignShape";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function quadraticLatex(a, b, c) {
  const parts = [];
  parts.push(a === 1 ? "x^2" : a === -1 ? "-x^2" : `${a}x^2`);
  if (b !== 0) {
    const bPart = Math.abs(b) === 1 ? "x" : `${Math.abs(b)}x`;
    parts.push(b > 0 ? `+ ${bPart}` : `- ${bPart}`);
  }
  if (c !== 0) {
    parts.push(c > 0 ? `+ ${c}` : `- ${Math.abs(c)}`);
  }
  return parts.join(" ");
}

function lineLatex(a, b) {
  if (a === 0) return `${b}`;
  const aPart = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  if (b === 0) return aPart;
  return b > 0 ? `${aPart} + ${b}` : `${aPart} - ${Math.abs(b)}`;
}

function lineExpr(a, b) {
  if (a === 0) return `y=${b}`;
  const parts = [`${a}x`];
  if (b !== 0) parts.push(b > 0 ? `+${b}` : `${b}`);
  return `y=${parts.join("")}`;
}

function buildQuadraticFromVertex() {
  const xE = randInt(-4, 4);
  const yE = randInt(-4, 4);
  const qa = randInt(-2, 2, [0]);
  const qb = -2 * qa * xE;
  const qc = qa * xE * xE + yE;
  const derivativeA = 2 * qa;
  const derivativeB = qb;
  const extremumType = qa > 0 ? "minimum" : "maksimum";
  const leftSign = qa > 0 ? "-" : "+";
  const rightSign = qa > 0 ? "+" : "-";

  return {
    qa,
    qb,
    qc,
    xE,
    yE,
    derivativeA,
    derivativeB,
    extremumType,
    leftSign,
    rightSign,
    functionLatex: quadraticLatex(qa, qb, qc),
    derivativeLatex: lineLatex(derivativeA, derivativeB),
  };
}

export function generateTask() {
  const data = buildQuadraticFromVertex();
  return {
    type: "QuadraticExtremumTask",
    prompt: `Wyznacz ekstremum funkcji f(x) = ${data.functionLatex} metoda pochodnej. Podaj wspolrzedne punktu ekstremum oraz okresl, czy jest to minimum czy maksimum.`,
    answer: `x_E=${data.xE}, y_E=${data.yE}, ${data.extremumType}`,
    data,
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Wyznacz ekstremum funkcji:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>Podaj wspolrzedne punktu ekstremum oraz okresl jego typ.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
  return (
    <div className="space-y-3">
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="x_E =" /></div>
        <FractionInput
          value={value.answerX || ""}
          onChange={(next) => onChange({ ...value, answerX: next })}
          disabled={disabled}
          label="x_E"
        />
      </div>
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="y_E =" /></div>
        <FractionInput
          value={value.answerY || ""}
          onChange={(next) => onChange({ ...value, answerY: next })}
          disabled={disabled}
          label="y_E"
        />
      </div>
      <div>
        <div style={{ marginBottom: "0.35rem" }}>Typ ekstremum:</div>
        <select
          value={value.answerType || ""}
          onChange={(e) => onChange({ ...value, answerType: e.target.value })}
          disabled={disabled}
          className="input-secondary"
          style={{ maxWidth: 320 }}
        >
          <option value="">-- Wybierz odpowiedz --</option>
          <option value="minimum">minimum</option>
          <option value="maksimum">maksimum</option>
        </select>
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const xValue = userAnswer.answerX?.parsed;
  const yValue = userAnswer.answerY?.parsed;
  const typeValue = userAnswer.answerType;
  return typeof xValue === "number"
    && typeof yValue === "number"
    && Math.abs(xValue - data.xE) < 0.0001
    && Math.abs(yValue - data.yE) < 0.0001
    && typeValue === data.extremumType;
}

export function renderUserAnswer(_taskOrAnswer, maybeUserAnswer) {
  const userAnswer = maybeUserAnswer !== undefined ? maybeUserAnswer : _taskOrAnswer;
  if (!userAnswer || typeof userAnswer !== "object") return <i>(brak odpowiedzi)</i>;
  const rawX = userAnswer.answerX?.raw;
  const rawY = userAnswer.answerY?.raw;
  const type = userAnswer.answerType;
  if (!rawX && !rawY && !type) return <i>(brak odpowiedzi)</i>;
  return (
    <div className="space-y-2">
      {rawX ? <div><InlineMath math={formatLatexPreview(rawX, "x_E")} /></div> : <div><InlineMath math={"x_E = ?"} /></div>}
      {rawY ? <div><InlineMath math={formatLatexPreview(rawY, "y_E")} /></div> : <div><InlineMath math={"y_E = ?"} /></div>}
      <div>Typ ekstremum: <strong>{type || "?"}</strong></div>
      {rawX && rawY ? <div><InlineMath math={`E = (${formatLatexPreview(rawX)}, ${formatLatexPreview(rawY)})`} /></div> : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  return (
    <div className="space-y-1">
      <div><InlineMath math={`x_E = ${data.xE}`} /></div>
      <div><InlineMath math={`y_E = ${data.yE}`} /></div>
      <div>Typ ekstremum: <strong>{data.extremumType}</strong></div>
      <div><InlineMath math={`E = (${data.xE}, ${data.yE})`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p><strong>Krok 1:</strong> Liczymy pochodna funkcji.</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />

      <p><strong>Krok 2:</strong> Wyznaczamy miejsce zerowe pochodnej.</p>
      <BlockMath math={`${data.derivativeLatex} = 0`} />
      <BlockMath math={`x_E = ${data.xE}`} />
      <p>
        To samo <InlineMath math={"x_E"} /> otrzymalibysmy ze wzoru na wspolrzedna
        wierzcholka paraboli: <InlineMath math={"x_E = -\\frac{b}{2a}"} />.
      </p>

      <p><strong>Krok 3:</strong> Sprawdzamy zmiane znaku pochodnej na szkicu wykresu <InlineMath math={"f'(x)"} />.</p>
      <div style={{ maxWidth: 350, margin: "20px auto" }}>
        <LineSignShape
          increasing={data.derivativeA > 0}
          zeroLabel={String(data.xE)}
          leftSign={data.leftSign}
          rightSign={data.rightSign}
          width={350}
        />
      </div>
      <p>
        Na lewo od <InlineMath math={`x = ${data.xE}`} /> pochodna ma znak <strong>{data.leftSign}</strong>,
        a na prawo znak <strong>{data.rightSign}</strong>. To oznacza, ze funkcja
        {" "}
        <strong>{data.leftSign === "+" ? "rosnie" : "maleje"}</strong>,
        a potem
        {" "}
        <strong>{data.rightSign === "+" ? "rosnie" : "maleje"}</strong>.
        {" "}
        Dlatego w punkcie <InlineMath math={`x = ${data.xE}`} /> funkcja ma <strong>{data.extremumType}</strong>.
      </p>

      <p><strong>Krok 4:</strong> Liczymy druga wspolrzedna punktu ekstremum.</p>
      <BlockMath math={`f(${data.xE}) = ${data.yE}`} />
      <BlockMath math={`E = (${data.xE}, ${data.yE})`} />
    </div>
  );
}
